import { ReportingType } from '@/__generated__/graphql';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import Input from '@/components/basicComponents/input';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import Select from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import { useErrorMessages } from '@/hooks/i18n';
import { getEnumKeyByValue } from '@/shared/utils/enums';
import { themeVar } from '@/styles/theme';
import { ApolloQueryResult, useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { ReportingType as ReportingTypeEnum } from '@trace-8/trace8-schema';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { z } from 'zod/v4';
import { reportingTypesEnumMap } from '../constants';
import { EDIT_TENANT_MUTATION } from '../queries';
import { IAdvisoryFirms, ITenant } from '../types';

const reportingTemplateOptions = Object.entries(reportingTypesEnumMap).map(([key, value]) => createSimpleSelectOption(key, value));

const updateTenantSchema = z.object({
  name: z.string().min(1, { message: 'Required' }),
  reportingType: z.union([z.nativeEnum(ReportingTypeEnum), z.literal('')])
});

type UpdateTenantType = z.infer<typeof updateTenantSchema>;

interface EditTenantModalProps {
  isOpen: boolean;
  tenant: ITenant;
  refetchTenants: () => Promise<ApolloQueryResult<{ advisoryFirms: IAdvisoryFirms }>>;
  onClose: () => void;
}

const EditTenantModal = ({ isOpen, tenant, refetchTenants, onClose }: EditTenantModalProps) => {
  const { default: defaultErrorMessage } = useErrorMessages();
  const { control, reset, handleSubmit } = useForm<UpdateTenantType>({
    resolver: zodResolver(updateTenantSchema)
  });

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [editTenant] = useMutation(EDIT_TENANT_MUTATION);

  useEffect(() => {
    if (!tenant) return;

    const { name, reportingType } = tenant;
    reset({ name, reportingType: reportingType || '' });
  }, [tenant]);

  const onSave = async (data: UpdateTenantType) => {
    try {
      setErrorMessage(null);
      setLoading(true);

      const { name, reportingType } = data;

      await editTenant({
        variables: {
          data: {
            id: tenant?.id,
            name,
            reportingType: !!reportingType ? (getEnumKeyByValue(ReportingTypeEnum, reportingType) as ReportingType) : null
          }
        }
      });

      await refetchTenants();
      onClose();
    } catch (error) {
      setErrorMessage(defaultErrorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Advisory Firm"
      confirmButton={{
        name: 'Save',
        disabled: loading,
        onClick: handleSubmit(onSave)
      }}
      showCancelButton={!loading}
      isUseOutsideClick={!loading}
      showCloseIcon={!loading}
      showRequiredFields={!loading}
    >
      {loading ? (
        <ModalLoader customText="Updating tenant..." />
      ) : (
        <>
          {errorMessage && (
            <Banner
              icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
              title="Error"
              description={errorMessage}
              bgColor={themeVar('context.error')}
            />
          )}
          <ModalContainer>
            <Controller
              name="name"
              control={control}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Input
                  label="Name"
                  value={value || ''}
                  onChange={(e) => {
                    onChange(e.target.value);
                  }}
                  errorText={error?.message}
                  placeholder="Name of the firm"
                  isRequiredField
                />
              )}
            />
            <Controller
              name="reportingType"
              control={control}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Select
                  label="Reporting Template"
                  value={value}
                  options={[{ label: 'None', value: '' }, ...reportingTemplateOptions]}
                  setValue={onChange}
                  errorText={error?.message}
                  isRequiredField
                />
              )}
            />
          </ModalContainer>
        </>
      )}
    </ModalWrappedContent>
  );
};

export default EditTenantModal;

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 15px;
`;
